// SingleIndicatorDetails.js

import React, { useEffect, useContext, useState, useCallback } from 'react';
import ChartContainer from './ChartContainer';
import BinaryIndicatorBlock from './BinaryIndicatorBlock';
import { formatTrendScore, formatStatusScore } from '../utils/scoreFormatUtils';
import { Button } from '@mui/material';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import SDGSpinner from './SDGSpinner';
import { IndicatorsContext } from '../State/IndicatorsContext.ts';
import { GoalsContext } from '../State/GoalsContext';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import IndicatorTagBadge from './IndicatorTagBadge';
import StatusSemaphore from './StatusSemaphore';
import { isEuStatusUnavailable } from '../utils/euStatusUtils';
import { hasKeyFigure, formatKeyFigureDisplay, shouldShowKeyValuesSection } from '../utils/keyFigures';
import '../utils/sharedStyles.css';

/**
 * @param {object} props
 * @param {object} props.indicator
 * @param {boolean} [props.embeddedInAccordion] - When true (target accordion body),
 *   skip the duplicate title/h2 and nested card border; accordion header already
 *   shows the title. Standalone single-indicator views leave this false.
 */
const SingleIndicatorDetails = ({ indicator, embeddedInAccordion = false }) => {
    const { loading } = useContext(IndicatorsContext);
    const { goalType } = useContext(GoalsContext);
    const isUN = goalType === 'un';
    const [chartRefs, setChartRefs] = useState({});

    // Check if chart data is available
    const hasChartData = indicator?.chartData &&
                        indicator?.chartData?.labels &&
                        indicator?.chartData?.labels.length > 0 &&
                        indicator?.chartData?.datasets &&
                        indicator?.chartData?.datasets.length > 0;
    const graphList = (indicator?.chartGraphs && indicator.chartGraphs.length > 0)
        ? indicator.chartGraphs
        : (hasChartData ? [{ chartType: indicator.chartType, chartData: indicator.chartData, seriesDescription: indicator.chartSeriesDescription, isAssessmentSeries: true }] : []);
    const hasGraphList = graphList.length > 0;
    const isBinary = indicator?.indicatorType === 'binary';

    // Download functions
    const handleDownload = (indicatorId, type) => {
        const chartElement = chartRefs[indicatorId];
        if (!chartElement) return;

        html2canvas(chartElement, { useCORS: true, backgroundColor: "#fff" }).then(canvas => {
            canvas.toBlob(blob => {
                saveAs(blob, `indicator-${indicatorId}.png`);
            });
        });    
    };

    const handleDownloadData = (indicator) => {
        const csvContent = generateCSV(indicator);
        if (csvContent.length === 0) return;
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `indicator-${indicator.id}-data.csv`);
    };

    const generateCSV = (indicator) => {
        let csvContent = '';
        const dataType = indicator?.acf?.data_display_type || indicator?.acf?.graph_data_indicators?.data_display_type;
        const isBinaryIndicator = indicator?.indicatorType === 'binary';

        if (isBinaryIndicator && indicator?.chartData?.labels?.length && indicator?.chartData?.datasets?.[0]?.data) {
            const labels = indicator.chartData.labels;
            const values = indicator.chartData.datasets[0].data;
            csvContent += 'Year,Status\n';
            labels.forEach((year, index) => {
                const v = values[index];
                const status = v != null && Number(v) >= 0.5 ? 'Yes' : 'No';
                csvContent += `${year},${status}\n`;
            });
            return csvContent;
        }

        if (dataType === 'line-graph-bar-chart' || indicator?.chartData) {
            try {
                if (indicator?.chartData) {
                    // Use chartData from preview endpoint
                    const labels = indicator.chartData.labels || [];
                    const datasets = indicator.chartData.datasets || [];
                    
                    // Header
                    const header = ['Year', ...datasets.map(d => d.label || 'Series')];
                    csvContent += header.join(',') + '\n';
                    
                    // Rows
                    labels.forEach((year, index) => {
                        const row = [year];
                        datasets.forEach(dataset => {
                            row.push(dataset.data?.[index] || '');
                        });
                        csvContent += row.join(',') + '\n';
                    });
                } else {
                    // Fallback to whole_data parsing
                const rawWhole = indicator?.acf?.graph_data_indicators?.whole_data || indicator?.acf?.whole_data || '';
                const mappedObject = JSON.parse(rawWhole);

                const labelsObject = mappedObject.columns?.find(column => column.label === "Year");
                const labelsArray = labelsObject ? labelsObject.data : (mappedObject.columns?.[0]?.data || []);
                const otherLabels = mappedObject.columns?.filter(column => column.label !== "Year") || [];

                // Header
                const header = ['Year', ...otherLabels.map(col => col.label)];
                csvContent += header.join(',') + '\n';

                // Rows
                labelsArray.forEach((year, index) => {
                    const row = [year];
                    otherLabels.forEach(col => {
                        row.push(col.data?.[index] || '');
                    });
                    csvContent += row.join(',') + '\n';
                });
                }
            } catch (error) {
                console.error('Error generating CSV:', error);
                csvContent = 'Indicator,Value,Description\n';
                csvContent += `"${indicator.acf.index}","${indicator.title_render}","${indicator.acf.description}"\n`;
            }
        } else {
            // Fallback for other data types
            csvContent = 'Indicator,Value,Description\n';
            csvContent += `"${indicator.acf.index}","${indicator.title_render}","${indicator.acf.description}"\n`;
        }
        
        return csvContent;
    };

    // Memoize the setChartRef function to prevent infinite re-renders
    const handleSetChartRef = useCallback((ref) => {
        if (indicator) {
            setChartRefs(prev => ({ ...prev, [indicator.id]: ref }));
        }
    }, [indicator?.id]);

    useEffect(() => {
        if (!indicator) {
            // Fetch indicator details if needed
        }
    }, [indicator]);

    if (loading) {
        return <SDGSpinner size="md" text="Loading indicator details..." />;
    }

    if (!indicator) {
        return <p>Please select an indicator on the left-hand side to view details.</p>;
    }

    // Get trend arrow and color
    const getTrendArrow = (trend) => {
        if (!trend) return { arrow: '—', color: '#bdbdbd' };
        const cat = String(trend).toLowerCase();
        if (cat.includes('on track') || cat.includes('target met')) {
            return { arrow: '↑', color: '#16a34a' };
        } else if (cat.includes('moderate')) {
            return { arrow: '↗', color: '#fcc30c' };
        } else if (cat.includes('marginal') || cat.includes('stagnation') || cat.includes('stagnating')) {
            return { arrow: '→', color: '#f67c01' };
        } else if (cat.includes('regression') || cat.includes('decreasing')) {
            return { arrow: '↓', color: '#d3302f' };
        }
        return { arrow: '—', color: '#bdbdbd' };
    };

    const trendInfo = getTrendArrow(indicator?.computed?.trend);
    const statusScore = indicator?.computed?.status_score ?? null;
    const euStatusDetails = indicator?.computed?.eu_status_details || null;

    // UN: status hidden (progress only). EU: benchmark quintile vs comparison countries (no numeric target).
    const showEuStatusComparison =
        !isUN &&
        indicator?.computed?.semaphore_status &&
        !isEuStatusUnavailable(indicator.computed.semaphore_status);
    const showEuStatusUnavailable =
        !isUN && !showEuStatusComparison;
    
    const showKeyValues = shouldShowKeyValuesSection(indicator?.computed);
    const hasProgressContent = indicator?.computed?.trend || showEuStatusComparison || showEuStatusUnavailable || showKeyValues || !!indicator?.computed?.availability_message;
    const metadataList = indicator?.acf?.metadata || indicator?.acf?.indicator_metadata || [];
    const metadataHasSourceLink = Array.isArray(metadataList) && metadataList.some(
        (item) => item?.key === 'Source' && item?.is_link
    );
    const hasReferencesContent = metadataList.length > 0 ||
        indicator?.acf?.source || indicator?.acf?.reference_field;
    const hasDescription = Boolean(indicator?.acf?.description);
    // Accordion already shows the title in the header; keep About only for description.
    const showAboutSection = embeddedInAccordion ? hasDescription : true;

    return (
        <div className={`indicator-details-container${embeddedInAccordion ? ' indicator-details-container--embedded' : ''}`}>
            {/* Nested card border only for standalone single-indicator view;
                accordion body already sits inside IndicatorDetails' card-container. */}
            <div className={embeddedInAccordion ? 'indicator-details-embedded' : 'card-container'}>
                {/* ——— Section: About ——— */}
                {showAboutSection && (
                <section className="indicator-detail-section indicator-detail-section--about">
                    {!embeddedInAccordion && (
                        <h2 className="title-h2 indicator-detail-title">
                            {indicator.title_render || indicator.title || 'Indicator'}
                            {!isUN && <IndicatorTagBadge tag={indicator.indicatorTag || indicator.indicator_tag} />}
                        </h2>
                    )}
                    {hasDescription && (
                        <div className="indicator-detail-description">
                            <h6 className="sdg-section-title">Description</h6>
                            <p className="text-body indicator-detail-description-text">
                                {indicator.acf.description}
                            </p>
                        </div>
                    )}
                </section>
                )}

                {/* ——— Section: Progress & assessment (two separate assessment-style blocks) ——— */}
                {hasProgressContent && (
                    <section className="indicator-detail-section indicator-detail-section--progress" aria-label="Progress and assessment">
                        <h6 className="sdg-section-title indicator-detail-section-heading">Progress & assessment</h6>
                        <div className="target-assessment-container">
                        {/* Block 1: Progress (same style as Target Assessment boxes) */}
                        <div className="assessment-box indicator-assessment-block target-assessment-col">
                            <h6 className="assessment-box-title">Progress</h6>
                            {indicator?.computed?.trend ? (
                                <div className="indicator-progress-detail">
                                    <span style={{ color: trendInfo.color, fontSize: '1.5rem', fontWeight: 900, marginRight: '6px', minWidth: '1.5rem', textAlign: 'center', fontFamily: 'system-ui, -apple-system, sans-serif', display: 'inline-block' }}>
                                        {trendInfo.arrow}
                                    </span>
                                    <span className="badge-pill-trend">{indicator.computed.trend}</span>
                                    {indicator.computed.trend_score != null && (
                                        <span className="text-muted" style={{ marginLeft: '4px' }}>(score {formatTrendScore(indicator.computed.trend_score)} / 5)</span>
                                    )}
                                </div>
                            ) : (
                                <p className="text-muted mb-0">
                                    {indicator?.computed?.availability_message
                                        || (isUN ? 'Kosovo data not available' : 'Progress not available for this indicator.')}
                                </p>
                            )}
                        </div>

                        {!isUN && (
                        <div className="assessment-box indicator-assessment-block target-assessment-col">
                            <h6 className="assessment-box-title">Kosovo status</h6>
                            {showEuStatusComparison ? (
                                <>
                                    <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '10px' }}>
                                        Compared to {euStatusDetails?.benchmark_group_name || 'EU27'}.
                                        Kosovo value year{' '}
                                        {euStatusDetails?.kosovo_year || indicator?.computed?.latest?.year || '—'};
                                        EU27 comparison year{' '}
                                        {euStatusDetails?.comparison_year || '—'}.
                                        Uses the headline Eurostat series only; other breakdowns appear in the chart only.
                                    </p>
                                    <div className="indicator-progress-at-a-glance" style={{ alignItems: 'center', gap: '10px' }}>
                                        <StatusSemaphore
                                            category={indicator.computed.semaphore_status}
                                            score={statusScore}
                                            size={28}
                                            showCaption={false}
                                        />
                                        <div>
                                            <span className="badge-pill">{indicator.computed.semaphore_status}</span>
                                            {statusScore != null && (
                                                <span className="text-muted" style={{ marginLeft: '6px' }}>
                                                    (score {formatStatusScore(statusScore)} / 100)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="indicator-status-not-available">
                                    <div className="indicator-status-not-available-inner">
                                        <p className="indicator-status-not-available-title">
                                            {indicator?.computed?.availability_message || 'Status not available'}
                                        </p>
                                        <p className="indicator-status-not-available-text">
                                            {indicator?.computed?.availability_message
                                                ? (indicator.computed.eu_data_status === 'not_relevant_for_kosovo'
                                                    ? 'This Eurostat series uses marine/fishing regions rather than countries, so Kosovo cannot be placed in an EU country distribution.'
                                                    : 'Kosovo values can be entered manually by KAS when available. EU comparison status is shown only when enough country data exist.')
                                                : 'Cross-country status needs enough comparison-country data for this indicator in the same year. Publish Kosovo values and re-fetch comparison countries if you recently ingested data.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        {/* Block 2: Key values (same assessment-box style) – only show Target when a target value is set */}
                        {showKeyValues && (
                            <div className="assessment-box indicator-assessment-block target-assessment-col">
                                <h6 className="assessment-box-title">Key values</h6>
                                <div className="key-values-grid">
                                    {hasKeyFigure(indicator?.computed?.baseline) && (
                                        <div className="key-value-item">
                                            <div className="key-value-item-label">Baseline</div>
                                            <div className="key-value-item-value key-value-item-value--baseline">
                                                {formatKeyFigureDisplay(indicator.computed.baseline, isBinary)}
                                            </div>
                                        </div>
                                    )}
                                    {hasKeyFigure(indicator?.computed?.latest) && (
                                        <div className="key-value-item">
                                            <div className="key-value-item-label">Latest</div>
                                            <div className="key-value-item-value key-value-item-value--latest">
                                                {formatKeyFigureDisplay(indicator.computed.latest, isBinary)}
                                            </div>
                                        </div>
                                    )}
                                    {indicator?.computed?.target && indicator.computed.target.value != null && (
                                        <div className="key-value-item key-value-item--target">
                                            <div className="key-value-item-label">Target</div>
                                            <div className="key-value-item-value key-value-item-value--target key-value-item-value--target-with-legend">
                                                <span className="chart-target-legend-line chart-target-legend-line--inline" aria-hidden="true" />
                                                <span className="key-value-item-value--target-number">{indicator.computed.target.value}</span>
                                                {indicator.computed.target.year != null && <span className="key-value-item-value--target-year"> by {indicator.computed.target.year}</span>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        </div>
                    </section>
                )}

                {/* ——— Section: Data (chart or binary block) ——— */}
                {(hasGraphList || isBinary) && (
                    <section className="indicator-detail-section indicator-detail-section--data" aria-label="Data and chart">
                        <h6 className="sdg-section-title indicator-detail-section-heading">Data</h6>
                        {isBinary ? (
                            <div className="indicator-chart-wrapper">
                                <BinaryIndicatorBlock indicator={indicator} />
                            </div>
                        ) : graphList.map((graph, graphIndex) => {
                            const seriesText = (graph.seriesDescription || graph.unitLabel || '').toString().trim();
                            const showAssessmentPill = graphList.length > 1 && !!graph.isAssessmentSeries;
                            const showSeriesPanel = seriesText !== '' || showAssessmentPill;
                            return (
                            <div key={graphIndex} className="indicator-chart-unit-block" style={graphList.length > 1 ? { marginBottom: '2rem' } : undefined}>
                                {showSeriesPanel && (
                                    <aside className="indicator-series-description-panel" aria-label="Series description">
                                        <div className="indicator-series-description-panel__title-row">
                                            <div className="indicator-series-description-panel__title">Series description</div>
                                            {showAssessmentPill && (
                                                <span className="eu-sdg-pill">Used for assessment</span>
                                            )}
                                        </div>
                                        {seriesText !== '' && (
                                            <p className="indicator-series-description-panel__body">{seriesText}</p>
                                        )}
                                    </aside>
                                )}
                                <div className="indicator-chart-wrapper">
                                    <ChartContainer
                                        indicator={indicator}
                                        chartType={graph.chartType || indicator.chartType}
                                        chartData={graph.chartData}
                                        unit={graph.unit || graph.unitLabel || graph.seriesDescription || null}
                                        setChartRef={graphIndex === 0 ? handleSetChartRef : undefined}
                                    />
                                </div>
                            </div>
                            );
                        })}
                        {!isBinary && indicator?.moeEnabled && typeof indicator?.moePercent === 'number' && (
                            <div className="indicator-moe-container">
                                <span className="indicator-moe-tag">
                                    Margin of error: {indicator.moeMode === 'up' ? '+' : indicator.moeMode === 'down' ? '−' : '±'}
                                    {indicator.moePercent}%
                                </span>
                            </div>
                        )}
                        <div className="indicator-detail-downloads">
                            <Button
                                startIcon={<FileDownloadOutlined />}
                                variant="outlined"
                                size="medium"
                                onClick={() => handleDownloadData(indicator)}
                                className="indicator-download-btn"
                            >
                                Download CSV
                            </Button>
                            {!isBinary && (
                                <Button
                                    startIcon={<FileDownloadOutlined />}
                                    variant="outlined"
                                    size="medium"
                                    onClick={() => handleDownload(indicator.id, 'image')}
                                    className="indicator-download-btn"
                                >
                                    Download PNG
                                </Button>
                            )}
                        </div>
                    </section>
                )}

                {/* ——— Section: Source & metadata ——— */}
                {hasReferencesContent && (
                    <section className="indicator-detail-section indicator-detail-section--references" aria-label="Source and metadata">
                        <h6 className="sdg-section-title indicator-detail-section-heading">Source & metadata</h6>
                        <div className="indicator-references-content">
                            {indicator.acf.reference_field && (
                                <div className="indicator-reference-block">
                                    <span className="indicator-reference-label">Reference</span>
                                    <p className="indicator-reference-text">{indicator.acf.reference_field}</p>
                                </div>
                            )}
                            {indicator.acf.source && !metadataHasSourceLink && (
                                <div className="indicator-reference-block">
                                    <span className="indicator-reference-label">Source</span>
                                    <a
                                        href={indicator.acf.source}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="metadata-link"
                                    >
                                        {indicator.acf.source}
                                    </a>
                                </div>
                            )}
                            {metadataList.length > 0 && (
                                <div className="metadata-container indicator-metadata">
                                    <div className="metadata-list">
                                        {metadataList.map((item, index) => (
                                            <div key={index} className="metadata-item">
                                                <div className="metadata-key">{item.key}</div>
                                                <div className="metadata-value">
                                                    {item.is_link ? (
                                                        <a href={item.value} target="_blank" rel="noopener noreferrer" className="metadata-link">{item.link_label || item.value}</a>
                                                    ) : item.is_html ? (
                                                        <div
                                                            className="metadata-value-html"
                                                            dangerouslySetInnerHTML={{ __html: item.value }}
                                                        />
                                                    ) : (
                                                        <span>{item.value}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default SingleIndicatorDetails;
